<script lang="ts">
  import { fade } from "svelte/transition";
  import { IconType, ButtonColor } from "$types/atoms";
  import Button from "$lib/atoms/Button.svelte";
  import { aspectRatio } from "$lib/utils/optimizeImage.js";
  import { modalContent, modalIsOpened } from "$stores";
  import { displayType } from "$lib/utils/breakpoints";
  import type {
    ModalStoryblok,
    ModalVideoStoryblok,
    VideoStoryblok,
  } from "$types/components";
  import { onMount } from "svelte";

  export let blok: VideoStoryblok | ModalVideoStoryblok;
  export let maxHeight: string = "85dvh";

  let videoEl: HTMLVideoElement;
  let active: boolean = false;
  let isSeeking: boolean = false;
  const isMobile = $displayType === "mobile";

  function togglePlayPause(e: MouseEvent) {
    if (blok.useModal && !isMobile) {
      if ($modalIsOpened) {
        if (!active) {
          videoEl.play();
          active = true;
          isSeeking = true;
        } else {
          videoEl.pause();
          active = false;
        }
      } else {
        e.preventDefault();

        active = true;
        isSeeking = true;

        $modalContent = {
          component: "modal",
          content: [
            {
              ...blok,
              autoplay: true,
              useModal: false,
              usePlaceholder: false,
              onEnd: () => {
                active = false;
                $modalContent = null;
                $modalIsOpened = false;
              },
            },
          ],
          showCloseButton: true,
          allowKeyboardInput: true,
          fadeIn: true,
        } as ModalStoryblok;

        $modalIsOpened = true;
      }
    } else {
      e.preventDefault();
      if (!active) {
        videoEl.play();
        active = true;
        isSeeking = true;
      } else {
        videoEl.pause();
        active = false;
      }
    }
  }

  let videoLoaded = false;

  onMount(() => {
    setTimeout(() => {
      if (videoEl && blok.autoplay ? true : !blok.usePlaceholder) {
        videoEl.play();
      }
    });
  });
</script>

{#if blok.media?.filename}
  <div
    class="relative rounded-[var(--border-radius-wrapper)] overflow-hidden"
    role="button"
    tabindex="0"
    on:click={(e) => {
      if (blok.usePlaceholder) {
        togglePlayPause(e);
      }
    }}
    on:keyup={() => {}}
  >
    <video
      bind:this={videoEl}
      on:fullscreenchange={(e) => {
        if (
          videoEl.paused &&
          !document.fullscreenElement &&
          typeof blok.onEnd === "function"
        ) {
          e.preventDefault();
          active = false;
          blok.onEnd();
        }
      }}
      on:ended={() => {
        active = false;
        if (typeof blok.onEnd === "function") {
          blok.onEnd();
        }
      }}
      on:play={() => {
        videoLoaded = true;
      }}
      style="width:100%;object-fit:cover;max-height:{maxHeight};"
      muted={blok.autoplay ? false : !blok.usePlaceholder}
      playsinline={blok.autoplay ? false : !blok.usePlaceholder}
      controls={blok.autoplay != null}
      loop={blok.autoplay ? false : !blok.usePlaceholder}
      src={isMobile
        ? blok.media.filename
        : blok.mediaDesktop?.filename || blok.media.filename}
    />

    {#if (blok.autoplay || !blok.usePlaceholder) && blok.placeholderMedia?.filename && !videoLoaded}
      <img
        class="absolute inset-0 object-cover h-full w-full"
        draggable="false"
        src={blok.placeholderMedia.filename.includes("storyblok")
          ? `${blok.placeholderMedia.filename}/m/`
          : blok.placeholderMedia.filename}
        alt={blok.placeholderMedia.alt}
        style={aspectRatio(blok.placeholderMedia.filename)}
      />
    {/if}

    {#if blok.usePlaceholder && !$modalIsOpened}
      {#if blok.placeholderMedia?.filename}
        {#if !active && !isSeeking}
          {#if blok.placeholderMedia.filename.includes(".mp4")}
            <video
              transition:fade|global
              class="absolute inset-0 object-cover h-full w-full"
              src={blok.placeholderMedia.filename}
              playsinline
              loop
              muted
              autoplay
            />
          {:else}
            <img
              transition:fade|global
              class="absolute inset-0 object-cover h-full w-full"
              draggable="false"
              src={blok.placeholderMedia.filename.includes("storyblok")
                ? `${blok.placeholderMedia.filename}/m/`
                : blok.placeholderMedia.filename}
              alt={blok.placeholderMedia.alt}
              style={aspectRatio(blok.placeholderMedia.filename)}
              loading="lazy"
            />
          {/if}
        {/if}
      {/if}

      <div
        class="controls select-none"
        class:hidden={blok.useModal ? $modalIsOpened : active}
      >
        <div class="controls-wrapper items-center">
          <Button color={ButtonColor.WHITE} icon={IconType.PLAY} reduced />
          <div class="video-info">
            {#if (blok.placeholderDuration?.length ?? 0) > 0}
              <p class="video-info-time type:section-title">
                {blok.placeholderDuration}
              </p>
            {/if}
            <p class="video-info-title type:section-title">
              {blok.placeholderTitle}
            </p>
          </div>
        </div>
      </div>
    {/if}

    {#if (blok.caption?.length ?? 0) > 0}
      <div
        class="caption type:body-medium"
        style="margin-top:clamp(10px, 0.8333333333vw, 12px)"
      >
        {blok.caption}
      </div>
    {/if}
  </div>
{/if}

<style lang="scss">
  .controls:not(.hidden) {
    display: flex;
    position: absolute;
    z-index: 99;
    bottom: 0;
    left: 24px;
    width: calc(100% - 48px);

    @include breakpoint("small") {
      left: 32px;
      width: calc(100% - 64px);
    }
  }

  .controls-wrapper {
    display: flex;
    margin-bottom: 32px;

    @include breakpoint("small") {
      margin-bottom: 40px;
    }
  }

  .video-info {
    color: rgb(var(--color-white));
    display: flex;
    flex-direction: column;
    margin-left: 24px;

    @include breakpoint("small") {
      margin-left: 32px;
    }

    .video-info-time {
      margin-bottom: 8px;
    }

    .video-info-time,
    .video-info-title {
      color: rgb(var(--color-white));
    }
  }
</style>
